import {
  Grid,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import dayjs from "dayjs";
import { getMovementType } from "helpers/groupBy";
import { MovementExportRow } from "types";

type HeaderProps = {
  fromDate: string;
  toDate: string;
  clientLabel: string;
};

type Props = HeaderProps & {
  movements: MovementExportRow[];
};

const formatDate = (date: string) =>
  date && dayjs(date).isValid() ? dayjs(date).format("DD/MM/YYYY") : "-";

const getMovementArticlesAmount = (movement: MovementExportRow) =>
  Number(movement.articles_amount ?? 0);

const formatTotal = (value: number) => value.toFixed(2);

export function OperationsListPrintHeader({
  fromDate,
  toDate,
  clientLabel,
}: HeaderProps) {
  const hasDateRange =
    fromDate && toDate && dayjs(fromDate).isValid() && dayjs(toDate).isValid();

  return (
    <Paper
      sx={{
        display: "flex",
        flexDirection: "column",
        justifyContent: "space-between",
        width: "100%",
        height: "100%",
        padding: "1rem",
        border: "1px solid grey",
        borderRadius: "0.5rem",
      }}
    >
      <Typography fontSize={20} variant="body1">
        <b>Movimientos</b>
      </Typography>
      <Typography fontSize={14} variant="body1">
        <b>Reporte:</b> Listado de movimientos
      </Typography>
      <Typography fontSize={14} variant="body1">
        <b>Cliente:</b> {clientLabel}
      </Typography>
      <Typography fontSize={14} variant="body1">
        <b>Periodo:</b>{" "}
        {hasDateRange
          ? `${dayjs(fromDate).format("DD/MM/YYYY")} a ${dayjs(toDate).format(
              "DD/MM/YYYY",
            )}`
          : "Todos"}
      </Typography>
    </Paper>
  );
}

export default function OperationsListPrint({
  movements,
  fromDate,
  toDate,
  clientLabel,
}: Props) {
  const totalPallets = movements.reduce((acc, mov) => acc + (mov.pallets || 0), 0);
  const totalWeight = movements.reduce((acc, mov) => acc + (mov.weight || 0), 0);
  const totalArticles = movements.reduce(
    (acc, mov) => acc + getMovementArticlesAmount(mov),
    0,
  );

  return (
    <Grid container spacing={2}>
      <Grid item xs={12}>
        <Grid
          container
          padding={2}
          component={Paper}
          sx={{
            border: "1px solid grey",
            borderRadius: "0.5rem",
          }}
        >
          <Grid item xs={4}>
            <Typography variant="body2">
              <b>Cliente:</b> {clientLabel}
            </Typography>
          </Grid>
          <Grid item xs={4}>
            <Typography variant="body2">
              <b>Desde:</b> {formatDate(fromDate)}
            </Typography>
          </Grid>
          <Grid item xs={4}>
            <Typography variant="body2">
              <b>Hasta:</b> {formatDate(toDate)}
            </Typography>
          </Grid>
        </Grid>
      </Grid>

      <Grid item xs={12}>
        <TableContainer
          component={Paper}
          sx={{
            border: "1px solid grey",
            borderRadius: "0.5rem",
            padding: "1rem",
            "@media print": {
              overflow: "visible",
              breakInside: "auto",
              pageBreakInside: "auto",
            },
          }}
        >
          <Typography variant="h5" mb={1}>
            Listado
          </Typography>
          <Table
            size="small"
            padding="none"
            sx={{
              "@media print": {
                tableLayout: "fixed",
                "& thead": { display: "table-header-group" },
                "& tr": {
                  breakInside: "avoid",
                  pageBreakInside: "avoid",
                },
                "& td, & th": {
                  breakInside: "avoid",
                  pageBreakInside: "avoid",
                  fontSize: "8px",
                  lineHeight: 1.25,
                  padding: "2px 3px",
                },
              },
            }}
          >
            <TableHead>
              <TableRow>
                <TableCell align="center" width="8%">
                  Tipo
                </TableCell>
                <TableCell align="center" width="7%">
                  N° Mov.
                </TableCell>
                <TableCell align="center" width="8%">
                  Fecha
                </TableCell>
                <TableCell align="center" width="13%">
                  Cliente
                </TableCell>
                <TableCell align="center" width="12%">
                  Cliente asociado
                </TableCell>
                <TableCell align="center" width="9%">
                  Doc. cliente
                </TableCell>
                <TableCell align="center" width="8%">
                  Remito
                </TableCell>
                <TableCell align="center" width="6%">
                  Pallets
                </TableCell>
                <TableCell align="center" width="7%">
                  Unidades
                </TableCell>
                <TableCell align="center" width="7%">
                  Peso
                </TableCell>
                <TableCell align="center" width="10%">
                  Observaciones
                </TableCell>
                <TableCell align="center" width="6%">
                  Depósito
                </TableCell>
                <TableCell align="center" width="5%">
                  Adic.
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {movements.map((movement) => (
                <TableRow key={movement.id}>
                  <TableCell align="center">
                    {getMovementType(movement.movement_type_id)}
                  </TableCell>
                  <TableCell align="center">{movement.id}</TableCell>
                  <TableCell align="center">{formatDate(movement.date)}</TableCell>
                  <TableCell align="center">
                    {movement.client?.razon_social || "-"}
                  </TableCell>
                  <TableCell align="center">
                    {movement.associated_client || "-"}
                  </TableCell>
                  <TableCell align="center">
                    {movement.client_document || "-"}
                  </TableCell>
                  <TableCell align="center">
                    {movement.numero_remito || "-"}
                  </TableCell>
                  <TableCell align="center">{movement.pallets || 0}</TableCell>
                  <TableCell align="center">
                    {getMovementArticlesAmount(movement)}
                  </TableCell>
                  <TableCell align="center">{movement.weight || 0}</TableCell>
                  <TableCell align="center">
                    {movement.observation || "-"}
                  </TableCell>
                  <TableCell align="center">{movement.store_id || "-"}</TableCell>
                  <TableCell align="center">
                    {movement.has_additional_movements ? "SI" : "NO"}
                  </TableCell>
                </TableRow>
              ))}
              <TableRow>
                <TableCell align="center" colSpan={7}>
                  <b>Total movimientos: {movements.length}</b>
                </TableCell>
                <TableCell align="center">
                  <b>{formatTotal(totalPallets)}</b>
                </TableCell>
                <TableCell align="center">
                  <b>{formatTotal(totalArticles)}</b>
                </TableCell>
                <TableCell align="center">
                  <b>{formatTotal(totalWeight)}</b>
                </TableCell>
                <TableCell align="center" colSpan={3} />
              </TableRow>
            </TableBody>
          </Table>
        </TableContainer>
      </Grid>
    </Grid>
  );
}
